// Build: v2.1
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import InteractiveSeatingChart from "@/components/InteractiveSeatingChart";
import { ScrollToTopButton } from "@/components/shared";

/* ============================================================================
   ELVIS ALOHA ARCHIVE — SINGLE-PAGE MARKETING LANDING
   
   Clean, professional one-page layout with:
   - White background
   - Hawaii-themed accents (tropical colors, subtle patterns)
   - All content in one scrollable view
   - Anchor navigation
   - Mobile-first responsive design
   ============================================================================ */

// Collection imagery paths
const IMAGES = {
  heroPrimary: "/assets/collection/real-graceland-costume-tickets-2.jpg",
  heroSecondary: "/assets/collection/real-proof-globe-portrait-1.jpg",
  documents: "/assets/collection/real-graceland-documents-1.jpg",
  artworkProofs: "/assets/collection/real-proof-artwork-grid.jpg",
  ticketDetail: "/assets/collection/real-graceland-costume-tickets-1.jpg",
  gracelandDisplay: "/assets/collection/real-graceland-framed-tickets-1.jpg"
};

/* ============================================================================
   REUSABLE ICON COMPONENTS (SVG)
   ============================================================================ */
const Icons = {
  Ticket: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
      <path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/>
    </svg>
  ),
  Document: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  ),
  Camera: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
      <circle cx="12" cy="13" r="3"/>
    </svg>
  ),
  Archive: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="5" x="2" y="3" rx="1"/>
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/>
      <path d="M10 12h4"/>
    </svg>
  ),
  ChevronDown: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6"/>
    </svg>
  ),
  ArrowRight: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
    </svg>
  ),
  MapPin: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  Calendar: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
      <line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/>
      <line x1="3" x2="21" y1="10" y2="10"/>
    </svg>
  ),
  Globe: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" x2="22" y1="12" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  )
};

/* ============================================================================
   SMOOTH SCROLL HANDLER
   ============================================================================ */
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }
};

/* ============================================================================
   STICKY NAVIGATION BAR
   ============================================================================ */
const StickyNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'collection', label: 'Collection' },
    { id: 'seating-chart', label: 'Seat Map' },
    { id: 'history', label: 'History' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'about', label: 'About' }
  ];

  return (
    <>
      <style>{`
        .landing-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          transition: all 0.3s ease;
        }
        .landing-nav.scrolled {
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
        }
        .landing-nav:not(.scrolled) {
          background: transparent;
        }
        .nav-link {
          position: relative;
          padding: 8px 16px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: #1a1a1a;
          text-decoration: none;
          cursor: pointer;
          transition: color 0.2s ease;
        }
        .nav-link:hover {
          color: #D85C4A;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background: #D85C4A;
          transition: all 0.3s ease;
          transform: translateX(-50%);
        }
        .nav-link:hover::after {
          width: 60%;
        }
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: flex !important; }
        }
        @media (min-width: 769px) {
          .desktop-nav { display: flex !important; }
          .mobile-toggle { display: none !important; }
        }
      `}</style>
      
      <nav className={`landing-nav ${scrolled ? 'scrolled' : ''}`} data-testid="landing-nav">
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Logo - Dark bg version (Elvis with hibiscus) when NOT scrolled, Light bg version when scrolled */}
          <a 
            href="#top" 
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}
          >
            <img 
              src={scrolled ? "/icons/logo-white-bg.png" : "/icons/logo-dark-bg.png"} 
              alt="Elvis Aloha Collection"
              style={{ height: '50px', width: 'auto' }}
            />
          </a>

          {/* Desktop Navigation */}
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {navLinks.map(link => (
              <button
                key={link.id}
                className="nav-link"
                onClick={() => scrollToSection(link.id)}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2">
              {mobileMenuOpen ? (
                <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
              ) : (
                <><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></>
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            borderTop: '1px solid #eee',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            padding: '16px'
          }}>
            {navLinks.map(link => (
              <button
                key={link.id}
                onClick={() => { scrollToSection(link.id); setMobileMenuOpen(false); }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '16px',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#1a1a1a',
                  cursor: 'pointer'
                }}
              >
                {link.label}
              </button>
            ))}
          </div>
        )}
      </nav>
    </>
  );
};

/* ============================================================================
   HERO SECTION
   ============================================================================ */
const HeroSection = () => (
  <section 
    id="top"
    style={{
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      background: 'linear-gradient(180deg, #fff 0%, #f8fafc 100%)',
      overflow: 'hidden'
    }}
    data-testid="hero-section"
  >
    {/* Subtle tropical accent - top right */}
    <div style={{
      position: 'absolute',
      top: 0,
      right: 0,
      width: '40%',
      height: '60%',
      background: 'linear-gradient(135deg, rgba(0, 166, 172, 0.03) 0%, transparent 60%)',
      pointerEvents: 'none'
    }} />
    
    <div style={{
      width: '100%',
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '120px 24px 80px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 450px), 1fr))',
      gap: '48px',
      alignItems: 'center'
    }}>
      {/* Text Content */}
      <div style={{ maxWidth: '600px' }}>
        <p style={{
          marginBottom: '16px',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '13px',
          fontWeight: 700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: '#00A6AC'
        }}>
          January 14, 1973 — Honolulu, Hawaii
        </p>
        
        <h1 style={{
          margin: '0 0 24px',
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(40px, 8vw, 72px)',
          fontWeight: 600,
          lineHeight: 1.05,
          color: '#1a1a1a',
          letterSpacing: '-0.02em'
        }}>
          Aloha from Hawaii
        </h1>
        
        <p style={{
          marginBottom: '16px',
          fontFamily: "'Source Serif 4', serif",
          fontSize: 'clamp(18px, 2.5vw, 22px)',
          lineHeight: 1.6,
          color: '#4a4a4a'
        }}>
          The world's first global satellite concert broadcast.
        </p>
        
        <p style={{
          marginBottom: '32px',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '16px',
          lineHeight: 1.7,
          color: '#666'
        }}>
          Explore the definitive collection of original tickets, production materials, 
          photographs, and documents preserving Elvis Presley's historic 1973 broadcast 
          from the Honolulu International Center Arena.
        </p>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '32px' }}>
          <button
            onClick={() => scrollToSection('collection')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '16px 32px',
              background: '#D85C4A',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            data-testid="hero-cta"
          >
            Explore Collection <Icons.ArrowRight />
          </button>
          
          <button
            onClick={() => scrollToSection('history')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '16px 32px',
              background: 'transparent',
              color: '#1a1a1a',
              border: '2px solid #e0e0e0',
              borderRadius: '4px',
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Learn the Story
          </button>
        </div>
        
        {/* Quick stats */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
          {[
            { icon: <Icons.MapPin />, label: 'Honolulu, HI' },
            { icon: <Icons.Calendar />, label: 'January 14, 1973' },
            { icon: <Icons.Globe />, label: '40+ Countries' }
          ].map((stat, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666' }}>
              <span style={{ color: '#00A6AC' }}>{stat.icon}</span>
              <span style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px' }}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Hero Image */}
      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'relative',
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          transform: 'rotate(1deg)'
        }}>
          <img 
            src={IMAGES.heroPrimary}
            alt="Original Aloha from Hawaii ticket collection"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '500px',
              objectFit: 'cover',
              borderRadius: '4px'
            }}
          />
          <p style={{
            marginTop: '12px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '13px',
            color: '#888',
            textAlign: 'center'
          }}>
            Documented at Graceland, September 14, 2022
          </p>
        </div>
        
        {/* Floating accent card */}
        <div style={{
          position: 'absolute',
          bottom: '-20px',
          left: '-20px',
          background: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
          transform: 'rotate(-2deg)'
        }}>
          <p style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: '28px',
            fontWeight: 700,
            color: '#D85C4A',
            margin: 0
          }}>
            50+
          </p>
          <p style={{
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: '#666',
            margin: 0
          }}>
            Original Tickets
          </p>
        </div>
      </div>
    </div>
    
    {/* Scroll indicator */}
    <button
      onClick={() => scrollToSection('collection')}
      style={{
        position: 'absolute',
        bottom: '40px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        animation: 'bounce 2s infinite'
      }}
      aria-label="Scroll to collection"
    >
      <Icons.ChevronDown />
      <style>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateX(-50%) translateY(0); }
          40% { transform: translateX(-50%) translateY(-10px); }
          60% { transform: translateX(-50%) translateY(-5px); }
        }
      `}</style>
    </button>
  </section>
);

/* ============================================================================
   COLLECTION OVERVIEW SECTION
   ============================================================================ */
const CollectionSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/collection/items")
      .then((res) => setItems(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = [
    { 
      icon: <Icons.Ticket />, 
      title: 'Original Tickets', 
      desc: 'Concert tickets, stubs, and venue credentials from the January 14, 1973 event',
      color: '#D85C4A'
    },
    { 
      icon: <Icons.Document />, 
      title: 'Production Materials', 
      desc: 'Contracts, correspondence, and planning documents from the broadcast',
      color: '#00A6AC'
    },
    { 
      icon: <Icons.Camera />, 
      title: 'Photographs', 
      desc: 'Original photographs and press materials from the historic concert',
      color: '#8B5CF6'
    },
    { 
      icon: <Icons.Archive />, 
      title: 'Memorabilia', 
      desc: 'Programs, posters, and promotional items from the Aloha event',
      color: '#F59E0B'
    }
  ];

  return (
    <section 
      id="collection"
      style={{
        padding: '100px 24px',
        background: 'white'
      }}
      data-testid="collection-section"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <p style={{
            marginBottom: '12px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#00A6AC'
          }}>
            The Archive
          </p>
          <h2 style={{
            margin: '0 0 16px',
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(32px, 6vw, 48px)',
            fontWeight: 500,
            color: '#1a1a1a'
          }}>
            A Definitive Collection
          </h2>
          <p style={{
            maxWidth: '600px',
            margin: '0 auto',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '17px',
            lineHeight: 1.7,
            color: '#666'
          }}>
            Preserving original artifacts, production materials, and historical documents 
            from the first satellite concert broadcast in history.
          </p>
        </div>

        {/* Category Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px',
          marginBottom: '64px'
        }}>
          {categories.map((cat, idx) => (
            <div 
              key={idx}
              style={{
                padding: '32px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${cat.color}15`,
                borderRadius: '12px',
                marginBottom: '20px',
                color: cat.color
              }}>
                {cat.icon}
              </div>
              <h3 style={{
                margin: '0 0 8px',
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '18px',
                fontWeight: 700,
                color: '#1a1a1a'
              }}>
                {cat.title}
              </h3>
              <p style={{
                margin: 0,
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '14px',
                lineHeight: 1.6,
                color: '#666'
              }}>
                {cat.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Featured Image */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
          gap: '48px',
          alignItems: 'center'
        }}>
          <div style={{
            position: 'relative',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
          }}>
            <img 
              src={IMAGES.gracelandDisplay}
              alt="The Aloha from Hawaii ticket collection displayed at Graceland"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
          
          <div>
            <h3 style={{
              margin: '0 0 16px',
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(28px, 4vw, 36px)',
              fontWeight: 500,
              color: '#1a1a1a'
            }}>
              Displayed at Graceland
            </h3>
            <p style={{
              marginBottom: '16px',
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '16px',
              lineHeight: 1.7,
              color: '#666'
            }}>
              The Elvis Aloha Archive preserves one of the most significant collections 
              of original Aloha from Hawaii concert materials. These items have been 
              documented and displayed at Graceland, establishing their authenticity 
              and historical importance.
            </p>
            <p style={{
              margin: 0,
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '16px',
              lineHeight: 1.7,
              color: '#666'
            }}>
              From original tickets to production documents, each artifact tells 
              part of the story of this groundbreaking television event.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   SEATING CHART SECTION - Explore Known Tickets by Seat
   ============================================================================ */
const SeatingChartSection = () => {
  const [showFullChart, setShowFullChart] = useState(false);
  
  return (
    <section 
      id="seating-chart"
      style={{
        padding: '100px 24px',
        background: '#fafafa'
      }}
      data-testid="seating-chart-section"
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <p style={{
            marginBottom: '12px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#C9A962'
          }}>
            Interactive Archive
          </p>
          <h2 style={{
            margin: '0 0 16px',
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(32px, 6vw, 48px)',
            fontWeight: 500,
            color: '#1a1a1a'
          }}>
            Explore the Known Tickets by Seat
          </h2>
          <p style={{
            margin: '0 auto',
            maxWidth: '700px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '17px',
            lineHeight: 1.7,
            color: '#666'
          }}>
            Navigate the arena seating chart to discover documented tickets from the 
            Aloha from Hawaii concert. Each marker represents a known ticket in the archive.
          </p>
        </div>
        
        {/* Chart Container */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 30px rgba(0,0,0,0.08)',
          border: '1px solid #e0e0e0'
        }}>
          {/* Embedded Chart */}
          <InteractiveSeatingChart embedded={true} />
        </div>
        
        {/* Quick Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginTop: '40px'
        }}>
          <StatCard label="Section AA" count={1} />
          <StatCard label="Section BB" count={38} />
          <StatCard label="Section H" count={44} />
          <StatCard label="Section L" count={53} subtitle="Stage-affected" />
        </div>
        
        {/* Attribution Note */}
        <div style={{
          marginTop: '32px',
          padding: '20px 24px',
          background: 'rgba(201, 169, 98, 0.08)',
          border: '1px solid rgba(201, 169, 98, 0.2)',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{
            margin: 0,
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '13px',
            color: '#8a7842',
            lineHeight: 1.6
          }}>
            <strong>Venue Reference Chart:</strong> This seating chart provides the underlying arena reference structure. 
            The Aloha Concert Reconstruction overlay is a developing research reconstruction based on surviving tickets, 
            photographs, production materials, and contemporary documentation.
          </p>
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ label, count, subtitle }) => (
  <div style={{
    padding: '24px',
    background: 'white',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    textAlign: 'center'
  }}>
    <div style={{
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '36px',
      fontWeight: 600,
      color: '#C9A962'
    }}>
      {count}
    </div>
    <div style={{
      fontFamily: "'Source Sans 3', sans-serif",
      fontSize: '14px',
      fontWeight: 600,
      color: '#1a1a1a'
    }}>
      {label}
    </div>
    {subtitle && (
      <div style={{
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: '12px',
        color: '#888',
        marginTop: '4px'
      }}>
        {subtitle}
      </div>
    )}
  </div>
);

/* ============================================================================
   HISTORY SECTION
   ============================================================================ */
const HistorySection = () => (
  <section 
    id="history"
    style={{
      padding: '100px 24px',
      background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)'
    }}
    data-testid="history-section"
  >
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '64px' }}>
        <p style={{
          marginBottom: '12px',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '13px',
          fontWeight: 700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: '#00A6AC'
        }}>
          The Story
        </p>
        <h2 style={{
          margin: '0 0 24px',
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(32px, 6vw, 48px)',
          fontWeight: 500,
          color: '#1a1a1a'
        }}>
          A Historic Broadcast
        </h2>
      </div>

      <div style={{
        fontFamily: "'Source Serif 4', serif",
        fontSize: '18px',
        lineHeight: 1.8,
        color: '#444'
      }}>
        <p style={{ marginBottom: '24px' }}>
          On January 14, 1973, Elvis Presley took the stage at the Honolulu International 
          Center Arena for what would become the first entertainment special broadcast 
          globally via satellite. The <em>Aloha from Hawaii Via Satellite</em> concert 
          was transmitted live to participating markets across Asia and Oceania.
        </p>
        
        <p style={{ marginBottom: '24px' }}>
          The production was an unprecedented technical achievement. The concert was 
          beamed via the Intelsat IV satellite to countries including Japan, South Korea, 
          the Philippines, Australia, and New Zealand. The United States broadcast 
          followed on April 4, 1973.
        </p>
        
        <p style={{ marginBottom: '24px' }}>
          The Elvis Aloha Archive preserves contemporaneous materials from this historic 
          event—original tickets, production documents, photographs, and artifacts that 
          document the planning, execution, and cultural impact of the broadcast.
        </p>

        <blockquote style={{
          margin: '48px 0',
          padding: '32px 40px',
          background: 'white',
          borderLeft: '4px solid #D85C4A',
          borderRadius: '0 8px 8px 0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}>
          <p style={{
            margin: 0,
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(20px, 3vw, 26px)',
            fontStyle: 'italic',
            color: '#1a1a1a',
            lineHeight: 1.5
          }}>
            "This independent archive distinguishes documented fact from unverified repetition, 
            classifying each claim by primary source evidence."
          </p>
        </blockquote>

        <p>
          Our methodology prioritizes contemporaneous 1972-73 evidence over later materials. 
          Open questions are acknowledged rather than filled with speculation, ensuring 
          the historical record remains accurate and verifiable.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   TIMELINE SECTION
   ============================================================================ */
const TimelineSection = () => {
  const events = [
    { date: '1972', title: 'Planning Begins', desc: 'NBC and RCA begin planning the satellite broadcast' },
    { date: 'Jan 12, 1973', title: 'Dress Rehearsal', desc: 'Full dress rehearsal concert at the arena' },
    { date: 'Jan 14, 1973', title: 'Satellite Concert', desc: 'Live broadcast to Asia and Oceania' },
    { date: 'Feb 4, 1973', title: 'Proceeds Donated', desc: '$77,000 presented to Kui Lee Cancer Fund' },
    { date: 'Apr 4, 1973', title: 'US Broadcast', desc: 'NBC airs the special in the United States' }
  ];

  return (
    <section 
      id="timeline"
      style={{
        padding: '100px 24px',
        background: 'white'
      }}
      data-testid="timeline-section"
    >
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <p style={{
            marginBottom: '12px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#00A6AC'
          }}>
            Key Moments
          </p>
          <h2 style={{
            margin: 0,
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(32px, 6vw, 48px)',
            fontWeight: 500,
            color: '#1a1a1a'
          }}>
            Timeline of Events
          </h2>
        </div>

        <div style={{ position: 'relative' }}>
          {/* Vertical line */}
          <div style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: '2px',
            background: '#e5e7eb',
            transform: 'translateX(-50%)'
          }} />

          {events.map((event, idx) => (
            <div 
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: idx === events.length - 1 ? 0 : '48px',
                flexDirection: idx % 2 === 0 ? 'row' : 'row-reverse'
              }}
            >
              {/* Content */}
              <div style={{
                flex: 1,
                padding: idx % 2 === 0 ? '0 48px 0 0' : '0 0 0 48px',
                textAlign: idx % 2 === 0 ? 'right' : 'left'
              }}>
                <p style={{
                  margin: '0 0 4px',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '13px',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: '#D85C4A'
                }}>
                  {event.date}
                </p>
                <h3 style={{
                  margin: '0 0 8px',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1a1a1a'
                }}>
                  {event.title}
                </h3>
                <p style={{
                  margin: 0,
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '14px',
                  color: '#666'
                }}>
                  {event.desc}
                </p>
              </div>

              {/* Dot */}
              <div style={{
                width: '16px',
                height: '16px',
                background: '#D85C4A',
                borderRadius: '50%',
                border: '4px solid white',
                boxShadow: '0 0 0 2px #D85C4A',
                flexShrink: 0,
                position: 'relative',
                zIndex: 1
              }} />

              {/* Spacer */}
              <div style={{ flex: 1 }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   GALLERY SECTION
   ============================================================================ */
const GallerySection = () => {
  const galleryImages = [
    { src: IMAGES.heroPrimary, title: 'Ticket Collection', desc: 'Original concert tickets' },
    { src: IMAGES.heroSecondary, title: 'Globe Portrait', desc: 'Production proof artwork' },
    { src: IMAGES.documents, title: 'Documents', desc: 'Production materials' },
    { src: IMAGES.artworkProofs, title: 'Artwork Proofs', desc: 'Printing color tests' },
    { src: IMAGES.ticketDetail, title: 'Ticket Details', desc: 'Design variations' },
    { src: IMAGES.gracelandDisplay, title: 'Graceland Display', desc: 'Museum presentation' }
  ];

  return (
    <section 
      id="gallery"
      style={{
        padding: '100px 24px',
        background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)'
      }}
      data-testid="gallery-section"
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <p style={{
            marginBottom: '12px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#00A6AC'
          }}>
            Visual Archive
          </p>
          <h2 style={{
            margin: 0,
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(32px, 6vw, 48px)',
            fontWeight: 500,
            color: '#1a1a1a'
          }}>
            Collection Gallery
          </h2>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '24px'
        }}>
          {galleryImages.map((img, idx) => (
            <div 
              key={idx}
              style={{
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
              }}
            >
              <div style={{ position: 'relative', paddingTop: '75%', overflow: 'hidden' }}>
                <img 
                  src={img.src}
                  alt={img.title}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  loading="lazy"
                />
              </div>
              <div style={{ padding: '20px' }}>
                <h3 style={{
                  margin: '0 0 4px',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#1a1a1a'
                }}>
                  {img.title}
                </h3>
                <p style={{
                  margin: 0,
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '13px',
                  color: '#888'
                }}>
                  {img.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   ABOUT / METHODOLOGY SECTION
   ============================================================================ */
const AboutSection = () => (
  <section 
    id="about"
    style={{
      padding: '100px 24px',
      background: 'white'
    }}
    data-testid="about-section"
  >
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '64px' }}>
        <p style={{
          marginBottom: '12px',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '13px',
          fontWeight: 700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: '#00A6AC'
        }}>
          Our Approach
        </p>
        <h2 style={{
          margin: 0,
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(32px, 6vw, 48px)',
          fontWeight: 500,
          color: '#1a1a1a'
        }}>
          Evidence-Driven Research
        </h2>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '32px'
      }}>
        {[
          {
            title: 'Tiered Sources',
            desc: 'Contemporaneous 1972-73 evidence takes precedence over later materials. Primary sources are prioritized.'
          },
          {
            title: 'Cross-Referenced',
            desc: 'Claims are verified against primary newspaper, broadcast, and artifact records from multiple sources.'
          },
          {
            title: 'Documented Gaps',
            desc: 'Open questions are acknowledged rather than filled with speculation. Uncertainty is stated clearly.'
          },
          {
            title: 'Owner Verification',
            desc: 'Physical artifact details require direct confirmation before being stated as established fact.'
          }
        ].map((item, idx) => (
          <div 
            key={idx}
            style={{
              padding: '32px',
              background: '#f8fafc',
              borderRadius: '12px',
              borderTop: '3px solid #00A6AC'
            }}
          >
            <h3 style={{
              margin: '0 0 12px',
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '18px',
              fontWeight: 700,
              color: '#1a1a1a'
            }}>
              {item.title}
            </h3>
            <p style={{
              margin: 0,
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '15px',
              lineHeight: 1.6,
              color: '#666'
            }}>
              {item.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================================
   FOOTER
   ============================================================================ */
const LandingFooter = () => (
  <footer style={{
    padding: '80px 24px 40px',
    background: '#1a1a1a',
    color: 'white'
  }}>
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '48px',
        marginBottom: '48px'
      }}>
        {/* Brand */}
        <div>
          <img 
            src="/icons/logo-dark-bg.png"
            alt="Elvis Aloha Collection"
            style={{ height: '60px', width: 'auto', marginBottom: '16px' }}
          />
          <p style={{
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '14px',
            lineHeight: 1.7,
            color: '#999',
            maxWidth: '280px'
          }}>
            Preserving the documented history of Elvis Presley's historic 
            1973 Aloha from Hawaii broadcast.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h4 style={{
            margin: '0 0 16px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: '#00A6AC'
          }}>
            Navigate
          </h4>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {['collection', 'history', 'timeline', 'gallery', 'about'].map(id => (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textAlign: 'left',
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: '14px',
                  color: '#ccc',
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {id}
              </button>
            ))}
          </nav>
        </div>

        {/* Legal */}
        <div>
          <h4 style={{
            margin: '0 0 16px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: '#00A6AC'
          }}>
            Legal
          </h4>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <a href="/rights" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: '#ccc', textDecoration: 'none' }}>Rights & Reproductions</a>
            <a href="/accessibility" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: '#ccc', textDecoration: 'none' }}>Accessibility</a>
            <a href="/privacy" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: '#ccc', textDecoration: 'none' }}>Privacy Policy</a>
          </nav>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{
        paddingTop: '32px',
        borderTop: '1px solid #333'
      }}>
        <p style={{
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '12px',
          lineHeight: 1.7,
          color: '#888',
          marginBottom: '16px'
        }}>
          <strong style={{ color: '#00A6AC' }}>Independent Historical Archive:</strong> Elvis Aloha Archive 
          is an independent historical and educational archive. It is not affiliated with, endorsed by, 
          sponsored by, or officially connected with Elvis Presley Enterprises, Graceland, Sony Music 
          Entertainment, or any official Elvis Presley organization.
        </p>
        <p style={{
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '13px',
          color: '#666'
        }}>
          © {new Date().getFullYear()} Elvis Aloha Archive. Independent historical archive.
        </p>
      </div>
    </div>
  </footer>
);

/* ============================================================================
   MAIN LANDING PAGE COMPONENT
   ============================================================================ */
const LandingPage = () => {
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Elvis Aloha Collection - Alternate View</title>
      </Helmet>
      <div data-testid="landing-page" style={{ background: 'white' }}>
        <StickyNav />
        <HeroSection />
        <CollectionSection />
        <SeatingChartSection />
        <HistorySection />
        <TimelineSection />
        <GallerySection />
        <AboutSection />
        <LandingFooter />
        
        {/* Scroll to top button */}
        <ScrollToTopButton threshold={500} bottomOffset={24} rightOffset={24} />
      </div>
    </>
  );
};

export default LandingPage;
